/**
 * Player browser pixel event mapper (P8-F7b, slice 2 — player integration).
 *
 * Maps our internal funnel event types to Meta standard events and fires them
 * via the fbq global (loaded by loadPixels when consent is granted).
 *
 * WEB-2409 п.1: те же события воронки уходят и в TikTok (`ttq.track`) — с
 * именем ТОЛЬКО из ответа сервера (`attribution.events.tiktok`) и тем же
 * ключом склейки, что у Meta и у серверной конверсии. Блока имён TikTok нет —
 * TikTok из браузера молчит: запасных имён для него на фронте нет, серверная
 * конверсия — источник правды.
 *
 * CRITICAL RULES:
 * - Purchase MUST NOT be fired in the player. It belongs ONLY on the success
 *   page via usePurchasePixel (ADR-0003 / P8 anti-pattern 4). Firing Purchase
 *   here breaks server↔browser deduplication.
 * - Имена событий приходят ГОТОВЫМИ от сервера (WEB-2181 Б,
 *   `attribution.events.meta` → setPlayerPixelEventNames); таблица
 *   PLAYER_EVENT_TO_META — запасной путь для старых закешированных ответов.
 * - Consent fail-closed (P8-F9): pixels load ONLY on an explicit 'granted'
 *   (loadPixels checks `consent !== 'granted'`). resolvePlayerConsent() itself
 *   reports the raw decision ('granted' | 'denied' | undefined) — see below.
 * - fbq global may be undefined (no consent → loadPixels is a no-op); always guard.
 */
import { readConsentDecision, type ConsentDecision } from './euConsent';
import { computeFunnelEventId } from './funnelEventId';
import { getSessionId } from './sessionId';

type MetaStandardEvent =
  | 'PageView'
  | 'ViewContent'
  | 'CompleteRegistration'
  | 'InitiateCheckout';

/**
 * Our funnel eventType → Meta standard event name.
 *
 * Events NOT in this map are silently skipped.
 * Purchase is intentionally absent — player must never fire it.
 * InitiateCheckout is intentionally absent — no checkout step in player today.
 */
export const PLAYER_EVENT_TO_META: Readonly<Record<string, MetaStandardEvent>> = {
  QUIZ_VIEW: 'PageView',
  SCREEN_VIEW: 'ViewContent',
  // WEB-2759 (решение владельца 06.10): запасные имена = серверные умолчания
  // справочника (`capi-our-event-catalog.ts`, defaults.meta) — иначе при
  // старом кэше браузер и сервер шлют одно событие под разными именами и Meta
  // его не склеивает. Держит сторож
  // `backend/src/attribution/utils/capi-our-event-catalog.browser-fallback-ends.web2759.spec.ts`.
  EMAIL_SUBMIT: 'CompleteRegistration',
  CHECKOUT_START: 'InitiateCheckout',
};

/**
 * WEB-2181 Б / ревизия Н3 — ПЛЕЕРНЫЙ ТИП → ВИД СОБЫТИЯ ВОРОНКИ.
 *
 * Вид (`FunnelConversionKind` на сервере) — это ОДНОВРЕМЕННО ключ строки в
 * готовых именах из публичного ответа и строка, от которой считается ключ
 * склейки браузерного и серверного события. Пока фронт считал ключ от своего
 * плеерного типа (`SCREEN_VIEW`), а сервер — от своего вида
 * (`QUIZ_PROGRESS_90`), ключи не совпадали: при включённом флаге серверной
 * отправки каждая конверсия считалась бы в кабинете клиента дважды.
 *
 * `QUIZ_VIEW` (стандартный PageView официальной вставки Meta) в таблице
 * намеренно отсутствует: серверного близнеца у него нет, склеивать нечего.
 *
 * Состав сторожится с двух концов из кода:
 * `playerPixelEvents.funnelKinds.web2181b.test.ts`.
 */
export const PLAYER_EVENT_TO_KIND: Readonly<Record<string, string>> = {
  // Просмотр содержимого = «взял порог 90 %»: тот же ФАКТ, что у сервера
  // (порог считает quizProgress90.ts, вызывающий — QuizScreenWizard).
  SCREEN_VIEW: 'QUIZ_PROGRESS_90',
  EMAIL_SUBMIT: 'LEAD',
  // Плеер это событие пока не шлёт (шага «начал оформление» в нём нет), но
  // имя и ключ для него уже разрешены: состав видов один на оба конца.
  CHECKOUT_START: 'CHECKOUT_START',
};

/**
 * WEB-2181 Б — ГОТОВЫЕ имена событий сети из публичного ответа сервера
 * (`attribution.events.meta`): «вид события → имя в сети», клиентское
 * переименование уже применено.
 *
 * Имена НЕ выдумываются на фронте: источник один — реестр сервера. Блока нет
 * (старый закешированный ответ, проект без подключения) — работают прежние
 * имена из таблицы выше.
 */
let networkEventNames: Readonly<Record<string, string>> | null = null;

export function setPlayerPixelEventNames(
  next: Readonly<Record<string, string>> | null | undefined,
): void {
  networkEventNames = next ?? null;
}

/**
 * WEB-2409 п.1 — ГОТОВЫЕ имена событий TikTok из публичного ответа сервера
 * (`attribution.events.tiktok`), «вид события → имя в сети». В отличие от
 * Meta, запасной таблицы нет: имя TikTok знает только сервер (клиент мог
 * переименовать событие в подключении), и браузерное событие под чужим именем
 * не склеится с серверным — лучше одна запись, чем две несклеенные.
 */
let tiktokEventNames: Readonly<Record<string, string>> | null = null;

export function setPlayerTikTokEventNames(
  next: Readonly<Record<string, string>> | null | undefined,
): void {
  tiktokEventNames = next ?? null;
}

/**
 * WEB-2409 п.1 — СОГЛАСИЕ ОТОЗВАНО на этой странице (`revokeTrackingConsent`,
 * «Отклонить» в баннере или постоянном элементе отзыва). Пока признак стоит,
 * события воронки не уходят НИ в одну сеть: штатного отзыва библиотек сетей
 * мало, если мы сами продолжаем звать `track`. Снимается новым «Принять»
 * (загрузчик с согласием `granted`).
 */
let trackingRevoked = false;

export function setPlayerTrackingRevoked(revoked: boolean): void {
  trackingRevoked = revoked;
}

/** Имя события TikTok: только имя от сервера по виду; нет — не шлём. */
function resolveTikTokEventName(eventType: string): string | undefined {
  // Просмотр страницы в TikTok шлёт базовая вставка (`ttq.page()` в
  // pixelLoader) — второго события здесь не будет: вида у QUIZ_VIEW нет.
  const kind = PLAYER_EVENT_TO_KIND[eventType];
  return kind ? tiktokEventNames?.[kind] : undefined;
}

/** Имя события Meta: имя от сервера по виду → прежнее имя из таблицы. */
function resolveMetaEventName(eventType: string): string | undefined {
  const fallback = PLAYER_EVENT_TO_META[eventType];
  if (!fallback) return undefined;
  const kind = PLAYER_EVENT_TO_KIND[eventType];
  const fromServer = kind ? networkEventNames?.[kind] : undefined;
  return fromServer ?? fallback;
}

// PageView is one-shot per page load (mirrors standard fbq pixel-init semantic).
const pageViewState = { fired: false };

/**
 * WEB-2182 — ОПОЗНАВАТЕЛЬНЫЕ ПРИЗНАКИ СОБЫТИЯ.
 *
 * До этой карточки события воронки уходили двумя аргументами: ни названия
 * квиза, ни ключа склейки. В кабинете все квизы клиента сливались в одну кучу,
 * а как только серверная отправка тех же событий появится (WEB-2184), без
 * ключа каждое событие посчиталось бы дважды.
 *
 * Признаки НЕ выдумываются на фронте и НЕ собираются из адресной строки: их
 * отдаёт сервер блоком `funnelIdentity` публичного ответа (WEB-2181), а
 * страница кладёт их сюда до загрузки пикселя.
 */
export interface PlayerPixelIdentity {
  readonly funnelId?: string | null;
  readonly funnelName?: string | null;
  readonly quizSlug?: string | null;
  readonly quizName?: string | null;
  readonly paywallSlug?: string | null;
  readonly paywallName?: string | null;
}

let identity: PlayerPixelIdentity | null = null;

export function setPlayerPixelIdentity(
  next: PlayerPixelIdentity | null | undefined,
): void {
  identity = next ?? null;
}

/**
 * Параметры события: название содержимого и его краткий адрес. Приоритет —
 * квиз, затем экран оплаты, затем воронка (у воронки краткого адреса в базе
 * нет, поэтому её опознаём идентификатором — см. WEB-2181).
 */
function identityParams(): Record<string, unknown> {
  if (!identity) return {};
  const name = identity.quizName ?? identity.paywallName ?? identity.funnelName;
  const id = identity.quizSlug ?? identity.paywallSlug ?? identity.funnelId;
  return {
    ...(name ? { content_name: name } : {}),
    ...(id ? { content_ids: [id] } : {}),
  };
}

/**
 * WEB-2409 п.1 — те же признаки в форме TikTok: у него идентификатор
 * содержимого — строка `content_id`, а не массив `content_ids`.
 */
function tiktokIdentityParams(): Record<string, unknown> {
  if (!identity) return {};
  const name = identity.quizName ?? identity.paywallName ?? identity.funnelName;
  const id = identity.quizSlug ?? identity.paywallSlug ?? identity.funnelId;
  return {
    ...(name ? { content_name: name } : {}),
    ...(id ? { content_id: id } : {}),
  };
}

/**
 * Событие «просмотр содержимого» — ОДИН раз за посещение (решение владельца
 * 17.09): раньше оно уходило на каждой смене экрана, и квиз из десяти экранов
 * давал одному человеку десять событий.
 *
 * Защёлка живёт в `sessionStorage` и привязана к идентификатору сессии:
 * перезагрузка страницы внутри посещения второго события не даёт, новое окно
 * инкогнито — даёт (новое посещение, новый счёт). Хранилища может не быть
 * (приватный режим, запрет) — тогда остаётся защёлка в памяти страницы.
 */
const ONCE_PER_VISIT: ReadonlySet<string> = new Set(['SCREEN_VIEW']);
const VISIT_KEY_PREFIX = 'qb_pixel_once_';
const firedThisPage = new Set<string>();

function visitKey(eventType: string): string {
  return `${VISIT_KEY_PREFIX}${eventType}`;
}

function alreadyFiredThisVisit(eventType: string): boolean {
  if (firedThisPage.has(eventType)) return true;
  try {
    return sessionStorage.getItem(visitKey(eventType)) === getSessionId();
  } catch {
    return false;
  }
}

function markFiredThisVisit(eventType: string): void {
  firedThisPage.add(eventType);
  try {
    sessionStorage.setItem(visitKey(eventType), getSessionId());
  } catch {
    /* хранилища нет — остаётся защёлка в памяти страницы */
  }
}

type Win = typeof globalThis & {
  fbq?: (...args: unknown[]) => void;
  /** Своя заглушка (массив) или чужая вставка — нужен только `track`. */
  ttq?: { track?: (...args: unknown[]) => void };
};

/**
 * Fire the Meta browser event that corresponds to our eventType.
 *
 * Форма вызова — та же, что у покупки (WEB-1319, usePurchasePixel):
 * `fbq('track', имя, {параметры}, {eventID})`. Ключ склейки обязан совпасть с
 * серверным посимвольно (`funnelEventId.ts` — побайтовое зеркало серверной
 * функции, золотые векторы в обеих кодовых базах), иначе сеть посчитает
 * событие дважды.
 *
 * No-ops when:
 *   - eventType has no Meta mapping (e.g. QUIZ_START, SCREEN_ANSWER)
 *   - fbq global is not injected (pixel not loaded — consent not granted)
 *   - PageView already fired this page load (one-shot idempotent)
 *   - ViewContent already fired this VISIT (решение владельца 17.09)
 *   - ключ склейки не посчитался, а у события есть серверная пара
 *     (`PLAYER_EVENT_TO_KIND`): его доставит сервер. Просмотр страницы пары
 *     не имеет и уходит без ключа.
 *
 * Возвращает промис (ключ считается через Web Crypto). Вызывающим ждать его не
 * обязательно — аналитика ничего не блокирует и НИКОГДА не бросает.
 *
 * WEB-2181 Б: имя события берётся из публичного ответа сервера
 * (`attribution.events.meta`, положено сюда setPlayerPixelEventNames), таблица
 * выше — запасной путь для ответов, закешированных до этой карточки.
 */
export async function fireMetaBrowserEvent(eventType: string): Promise<void> {
  const metaEvent = resolveMetaEventName(eventType);
  if (!metaEvent) return;
  // Согласие отозвано на этой странице — молчат обе сети (WEB-2409 п.1).
  if (trackingRevoked) return;

  const w = globalThis as Win;
  const fbq = typeof w.fbq === 'function' ? w.fbq : undefined;
  // WEB-2409 п.1: TikTok — только при готовом имени из ответа сервера и
  // установленной вставке (своя заглушка или чужая с методом track).
  const tiktokEvent = resolveTikTokEventName(eventType);
  const ttq = w.ttq;
  const ttqTrack =
    tiktokEvent && ttq && typeof ttq.track === 'function' ? ttq : undefined;
  if (!fbq && !ttqTrack) return;

  // Защёлки ставятся ДО ожидания ключа: два быстрых вызова подряд иначе
  // проскочили бы оба.
  // Защёлка PageView судится по ПЛЕЕРНОМУ типу, а не по имени: имя может быть
  // переименовано клиентом, а «один просмотр страницы на загрузку» — нет.
  if (eventType === 'QUIZ_VIEW') {
    if (pageViewState.fired) return;
    pageViewState.fired = true;
  }
  if (ONCE_PER_VISIT.has(eventType)) {
    if (alreadyFiredThisVisit(eventType)) return;
    markFiredThisVisit(eventType);
  }

  // Ключ склейки. Решение владельца 06.10.2026: события воронки уходят и с
  // сервера всегда, поэтому браузерная копия без ключа стала бы в сети вторым
  // событием. Ключ не посчитался (нет Web Crypto: адрес `http://` либо очень
  // старый браузер) — дальше по виду события:
  //  · серверная пара ЕСТЬ (событие в `PLAYER_EVENT_TO_KIND`) — браузер
  //    молчит, событие доставит сервер;
  //  · серверной пары НЕТ (просмотр страницы: сервер шлёт только порог 90 %,
  //    почту и начало оформления) — уходит как раньше, без ключа. Склеивать
  //    его не с чем, а молчание оставило бы сеть без просмотра вовсе.
  let eventId: string | undefined;
  try {
    // Ключ склейки считается от ВИДА события — ровно от той строки, от
    // которой его считает сервер (funnel-conversion-dispatch.service.ts).
    eventId = await computeFunnelEventId(
      getSessionId(),
      PLAYER_EVENT_TO_KIND[eventType] ?? eventType,
    );
  } catch {
    if (PLAYER_EVENT_TO_KIND[eventType]) return;
  }

  if (fbq) {
    try {
      // Без ключа (просмотр страницы без Web Crypto) четвёртый аргумент —
      // `undefined`: для вставки Meta это то же, что вызов без него.
      fbq(
        'track',
        metaEvent,
        identityParams(),
        eventId && { eventID: eventId },
      );
    } catch {
      // Never throw from analytics
    }
  }

  // WEB-2409 п.1: форма TikTok — `ttq.track(имя, {параметры}, {event_id})`,
  // ключ склейки третьим аргументом-опциями (как у покупки, usePurchasePixel).
  // Сбой одной сети не мешает другой: у каждой свой try. Имя TikTok есть
  // только у событий с серверной парой, а они без ключа сюда не доходят.
  if (ttqTrack && tiktokEvent) {
    try {
      ttqTrack.track?.(tiktokEvent, tiktokIdentityParams(), {
        event_id: eventId,
      });
    } catch {
      // Never throw from analytics
    }
  }
}

/**
 * Просмотр страницы после установки пикселя — как в канонической вставке Meta
 * (WEB-2182 п.6). Идёт тем же одноразовым путём, поэтому на квизе, где
 * просмотр уже отправляется отдельно (`PublicQuiz`), второго события не будет.
 */
export function fireMetaPageView(): Promise<void> {
  return fireMetaBrowserEvent('QUIZ_VIEW');
}

/**
 * Read player-page consent from the w2a_consent same-domain cookie.
 *
 * Returns the visitor's RAW decision: 'granted' | 'denied' | undefined
 * (undefined = no decision recorded yet — missing/empty/unknown cookie value).
 *
 * Ревизия 5 (дефект 1): раньше всё не-'granted' сворачивалось в undefined.
 * Явный отказ становился неотличим от «решения ещё нет» — и его нельзя было
 * отправить бэкенду как 'denied' в теле события (поле consent). Загрузка
 * пикселей остаётся fail-closed НЕ здесь, а у потребителя: loadPixels грузит
 * скрипты только при строгом `consent === 'granted'` (pixelLoader.ts:29-30),
 * поэтому 'denied' пикселей не включает.
 *
 * NOTE: On the success page (HandoffSuccess.tsx) the consent signal is
 * SERVER-derived and authoritative over cookies. Here on the player page,
 * no server consent is available pre-identity, so we fall back to the
 * same-domain cookie. The player runs on the primary domain where the
 * cookie is valid. EU-firing activates when F9 deposits w2a_consent=granted.
 */
export function resolvePlayerConsent(): ConsentDecision | undefined {
  if (typeof document === 'undefined') return undefined;
  return readConsentDecision();
}

/**
 * Test-only: reset one-shot guards between test cases.
 * `keepVisit: true` — «перезагрузили страницу внутри того же посещения»:
 * память страницы обнуляется, отметка посещения в sessionStorage остаётся.
 */
export function __resetPlayerPixelEventsForTests(
  options: { keepVisit?: boolean } = {},
): void {
  pageViewState.fired = false;
  firedThisPage.clear();
  trackingRevoked = false;
  if (options.keepVisit) return;
  for (const eventType of ONCE_PER_VISIT) {
    try {
      sessionStorage.removeItem(visitKey(eventType));
    } catch {
      /* хранилища нет — чистить нечего */
    }
  }
}
